import type { DistanceModel, Directivity } from '@sw/schema'
import { angleDifferenceDeg } from '@sw/geo'

export const dbToLinear = (db: number): number => 10 ** (db / 20)
export const linearToDb = (linear: number): number =>
  linear <= 0 ? Number.NEGATIVE_INFINITY : 20 * Math.log10(linear)

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

/**
 * Loudness at a distance, following the same three curves the Web Audio panner
 * uses so that the headless answer and the browser's rendering agree.
 *
 * One deliberate difference: `maxDistanceM` is treated as a hard cull radius
 * and returns silence, where Web Audio's inverse and exponential curves trail
 * off forever. A world with hundreds of emitters needs a definite answer to
 * "can this possibly be heard from here?", and the studio already draws that
 * radius as a ring on the map, so creators are choosing it as a real edge.
 */
export function distanceGain(distanceM: number, model: DistanceModel): number {
  if (distanceM >= model.maxDistanceM) return 0

  const reference = Math.max(model.refDistanceM, 1e-6)
  const distance = Math.max(distanceM, reference)

  switch (model.rolloff) {
    case 'inverse':
      return reference / (reference + model.rolloffFactor * (distance - reference))
    case 'linear': {
      const span = Math.max(model.maxDistanceM - reference, 1e-6)
      return clamp(1 - (model.rolloffFactor * (distance - reference)) / span, 0, 1)
    }
    case 'exponential':
      return (distance / reference) ** -model.rolloffFactor
  }
}

/**
 * How much a directional source is turned down for a listener off its axis.
 * `sourceToListenerDeg` is the bearing from the source to the listener, in the
 * same frame as the source's own heading.
 */
export function directivityGain(
  sourceToListenerDeg: number,
  directivity: Directivity,
): number {
  const off = Math.abs(angleDifferenceDeg(directivity.headingDeg, sourceToListenerDeg))
  const inner = directivity.innerAngleDeg / 2
  const outer = directivity.outerAngleDeg / 2

  if (off <= inner) return 1
  if (off >= outer) return dbToLinear(directivity.outerGainDb)

  const t = (off - inner) / Math.max(outer - inner, 1e-6)
  return 1 + t * (dbToLinear(directivity.outerGainDb) - 1)
}

export const SPEED_OF_SOUND_MPS = 343

/**
 * Playback rate from relative motion. `radialSpeedMps` is positive when the
 * source is receding.
 *
 * Clamped hard: a helicopter passing overhead should lift and drop, but an
 * unclamped rate turns a fast fly-by into a chipmunk, and one bad velocity
 * estimate from a jumpy GPS fix would otherwise be very audible.
 */
export function dopplerRate(radialSpeedMps: number, maxShift = 0.15): number {
  const denominator = SPEED_OF_SOUND_MPS + radialSpeedMps
  // Closing faster than sound is not something a sound world contains; it
  // means a jumpy fix produced a nonsense velocity. Guard it explicitly,
  // because the formula does not merely overshoot there — the denominator
  // goes negative and the pitch flips the wrong way, so an approaching
  // source would drop instead of rise.
  if (denominator <= 0) return 1 + maxShift

  return clamp(SPEED_OF_SOUND_MPS / denominator, 1 - maxShift, 1 + maxShift)
}
